import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { queryClient } from '@/queryclient';
import { useMutation, useQuery } from '@/utils/api';

interface MarkdownFileContent {
  content: string;
  lastModified: Date | null;
}

export function useGetMarkdownFile(fileName: string, email?: string) {
  const axios = useAxios({ email });

  return useQuery<MarkdownFileContent>({
    defaultErrorMessage: `Failed to fetch ${fileName}`,
    queryKey: ['markdown-file', fileName, email],
    queryFn: async () => {
      try {
        const res = await axios.get<{ url: string }>(`/file`, {
          params: { name: fileName },
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          return { content: '', lastModified: null };
        }

        const fileResponse = await fetch(presignedUrl);

        if (!fileResponse.ok) {
          return { content: '', lastModified: null };
        }

        const lastModifiedHeader = fileResponse.headers.get('Last-Modified');
        const lastModified = lastModifiedHeader
          ? new Date(lastModifiedHeader)
          : null;

        const content = await fileResponse.text();

        return { content: content || '', lastModified };
      } catch {
        return { content: '', lastModified: null };
      }
    },
    retry: 0,
  });
}

export function useUploadMarkdownFile(fileName: string, email?: string) {
  const axios = useAxios({ email });
  const { invalidate: invalidateMyFiles } = useMy(
    { resource: 'file' },
    { enabled: false, email }
  );

  return useMutation({
    defaultErrorMessage: `Failed to upload ${fileName}`,
    mutationFn: async (file: File | string) => {
      const content = typeof file === 'string' ? file : await file.text();

      const res = await axios.put<{ url: string }>(`/file`, null, {
        params: { name: fileName },
      });

      const uploadUrl = res.data.url;

      if (!uploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: content,
        headers: { 'Content-Type': 'text/markdown' },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      return null;
    },
    onSuccess: () => {
      toast.success(`${fileName} uploaded successfully`);
      queryClient.invalidateQueries({
        queryKey: ['markdown-file', fileName, email],
      });
      invalidateMyFiles();
    },
  });
}

export function useDeleteMarkdownFile(fileName: string, email?: string) {
  const axios = useAxios({ email });
  const { invalidate: invalidateMyFiles } = useMy(
    { resource: 'file' },
    { enabled: false, email }
  );

  return useMutation({
    defaultErrorMessage: `Failed to delete ${fileName}`,
    mutationFn: async () => {
      await axios.delete(`/file`, {
        params: { name: fileName },
      });
      return null;
    },
    onSuccess: () => {
      toast.success(`${fileName} deleted successfully`);
      queryClient.invalidateQueries({
        queryKey: ['markdown-file', fileName, email],
      });
      invalidateMyFiles();
    },
  });
}
